import * as Sentry from '@sentry/react';
import { ValidationError } from '../modules/error';

/**
 * 想定外エラーのみ Sentry に送信する #2883
 *
 * 想定内エラー（ValidationError）はユーザーにメッセージ表示済みで自己解決可能なため、
 * Sentry 通知をスキップしてノイズを抑制する。
 * catch 側では `Sentry.captureException` の代わりに本関数を利用する。
 */
export const captureUnexpectedException = (error: unknown): void => {
  if (error instanceof ValidationError) {
    return;
  }
  Sentry.captureException(error);
};

/**
 * エラーメッセージをユーザーフレンドリーなメッセージに変換する
 */
export const convertToUserFriendlyMessage = (error: unknown): string => {
  if (error instanceof Error) {
    const message = error.message;

    // ネットワークエラー
    if (message.includes('Failed to fetch') || message.includes('Network request failed')) {
      return '通信に失敗しました。インターネット接続を確認してください。';
    }

    // タイムアウトエラー
    if (message.includes('timeout') || message.includes('Timeout')) {
      return '処理がタイムアウトしました。もう一度お試しください。';
    }

    // 認証エラー
    if (message.includes('Unauthorized') || message.includes('認証')) {
      return '認証に失敗しました。再度ログインしてください。';
    }

    // Firebase Storageエラー
    if (message.includes('storage/retry-limit-exceeded') || message.includes('storage/unknown')) {
      return 'ファイルのアップロードに失敗しました。時間をおいて再度お試しください。';
    }
    if (message.includes('storage/unauthorized') || message.includes('storage/unauthenticated')) {
      return 'ファイルのアップロード権限がありません。再度ログインしてください。';
    }
    if (message.includes('storage/canceled')) {
      return 'ファイルのアップロードがキャンセルされました。';
    }
    if (message.includes('storage/')) {
      return 'ファイルの処理中にエラーが発生しました。もう一度お試しください。';
    }

    // 既にユーザーフレンドリーなメッセージの場合はそのまま返す
    if (
      message.includes('失敗しました') ||
      message.includes('できません') ||
      message.includes('エラー') ||
      message.includes('ログイン') ||
      message.includes('設定されていません')
    ) {
      return message;
    }

    // HTTPエラー
    if (message.includes('HTTP error') || message.includes('status:')) {
      return '処理に失敗しました。もう一度お試しください。';
    }

    // その他の技術的なエラーメッセージ
    if (
      message.includes('cloudfunctions.net') ||
      message.includes('TypeError') ||
      message.includes('ReferenceError') ||
      message.includes('SyntaxError')
    ) {
      return '処理中にエラーが発生しました。もう一度お試しください。';
    }

    // デフォルトのメッセージ
    return message;
  }

  // エラーオブジェクトでない場合
  return '予期しないエラーが発生しました。もう一度お試しください。';
};

/**
 * イベントハンドラー用のエラーキャプチャラッパー
 * onClick, onChange などのイベントハンドラー内のエラーをSentryに送信する
 */
export const captureError = <T extends (...args: never[]) => unknown>(
  handler: T,
): ((...args: Parameters<T>) => ReturnType<T>) => {
  return (...args: Parameters<T>): ReturnType<T> => {
    try {
      return handler(...args) as ReturnType<T>;
    } catch (error) {
      // 想定内エラーは Sentry に送らず、型・メッセージを保持したまま再スロー #2883
      if (error instanceof ValidationError) {
        throw error;
      }
      // Sentryにエラーを送信
      Sentry.captureException(error);
      // エラーをコンソールにも出力
      // eslint-disable-next-line no-console
      console.error('[ErrorHandler] Captured error:', error);
      // ユーザーフレンドリーなメッセージに変換してスロー
      const userFriendlyMessage = convertToUserFriendlyMessage(error);
      throw new Error(userFriendlyMessage);
    }
  };
};

/**
 * 非同期イベントハンドラー用のエラーキャプチャラッパー
 */
export const captureAsyncError = <T extends (...args: never[]) => Promise<unknown>>(
  handler: T,
): ((...args: Parameters<T>) => Promise<Awaited<ReturnType<T>>>) => {
  return async (...args: Parameters<T>): Promise<Awaited<ReturnType<T>>> => {
    try {
      return (await handler(...args)) as Awaited<ReturnType<T>>;
    } catch (error) {
      // 想定内エラーは Sentry に送らず、型・メッセージを保持したまま再スロー #2883
      if (error instanceof ValidationError) {
        throw error;
      }
      // Sentryにエラーを送信
      Sentry.captureException(error);
      // エラーをコンソールにも出力
      // eslint-disable-next-line no-console
      console.error('[ErrorHandler] Captured async error:', error);
      // ユーザーフレンドリーなメッセージに変換してスロー
      const userFriendlyMessage = convertToUserFriendlyMessage(error);
      throw new Error(userFriendlyMessage);
    }
  };
};
